import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    TableCell,
    TableRow,
    Box,
    Checkbox,
    IconButton,
    Tooltip,
    Menu,
    MenuItem,
    Typography,
    Badge,
} from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import UpdateIcon from "@mui/icons-material/Update";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import { formatDate } from 'utils/formatDate';
import {
    useRunTestCasesMutation,
    useStopTestCasesMutation,
    useUpdateTestCaseFeedbackMutation,
} from '../../queries';
import clsx from 'clsx';
import styles from './styles.module.scss';
import RunIcon from './icons/RunIcon';
import StopIcon from './icons/StopIcon';
import EditIcon from '@mui/icons-material/Edit';
import { TEST_CASE_STATUS } from '../../constants';
import { TEST_STATUS_STYLES } from 'theme/statusColors';
import EditTestCaseModal from 'components/common/EditTestCaseModal';
import { useNotification } from 'context/NotificationContext';

const TestCaseItem = ({
    journeyId,
    testCase,
    checkedIds = [],
    handleCheckboxChange,
    handleOpenDeleteDialog,
    onOpenRegenerateModal,
    disableActions = false,
}) => {
    const navigate = useNavigate();
    const showNotification = useNotification();

    const [anchorEl, setAnchorEl] = useState(null);
    const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
    const isMenuOpen = Boolean(anchorEl);

    const handleMenuOpen = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = (event) => {
        event?.stopPropagation();
        setAnchorEl(null);
    };

    const { mutateAsync: runTestCasesAsync, isPending: isRunPending } =
        useRunTestCasesMutation(journeyId);
    const { mutateAsync: stopTestCaseAsync, isPending: isStopPending } =
        useStopTestCasesMutation(journeyId);
    const { mutateAsync: updateFeedbackAsync, isPending: isFeedbackPending } =
        useUpdateTestCaseFeedbackMutation(journeyId);

    const isRunning = testCase?.status === TEST_CASE_STATUS.running;
    console.log("isRunning: ", isRunning);
    const isInitialFailure =
        testCase?.status === TEST_CASE_STATUS.initialFailure;
    const canRun =
        testCase &&
        [
            TEST_CASE_STATUS.draft,
            TEST_CASE_STATUS.failed,
            TEST_CASE_STATUS.completed,
            TEST_CASE_STATUS.initialFailure,
        ].includes(testCase.status);
    const runTooltip =
        testCase?.status === TEST_CASE_STATUS.draft ? "Approve and Run" : "Re-Run";

    const statusLabel = (() => {
        if (!testCase?.status) return "";
        if (testCase.status === TEST_CASE_STATUS.running) return "In Progress...";
        if (testCase.status === TEST_CASE_STATUS.pending) return "Pending";
        if (testCase.status === TEST_CASE_STATUS.completed) return "Passed";
        if (testCase.status === TEST_CASE_STATUS.initialFailure)
            return "Initial failure";
        return String(testCase.status).toLowerCase();
    })();

    const failureTooltip =
        'The test case failed on the first run, which indicates a potential issue in the positive flow. Negative scenarios were not generated automatically. You can review the test result, fix the issue and regenerate tests, or manually generate negative scenarios using the "Generate negative scenarios" button.';
    const isLikeActive = testCase?.my_feedback === 1;
    const isDislikeActive = testCase?.my_feedback === -1;

    const handleRun = async (e) => {
        e.stopPropagation();
        if (!testCase?.id) return;
        await runTestCasesAsync([testCase.id]);
    };

    const handleStop = async (e) => {
        e.stopPropagation();
        if (!testCase?.id) return;
        await stopTestCaseAsync(testCase.id);
    };

    const handleRegenerate = async () => {
        if (!testCase?.id) return;
        if (typeof onOpenRegenerateModal !== "function") return;
        onOpenRegenerateModal({
            idsToRegenerate: [testCase.id],
            regenerateAll: false,
        });
    };

    const handleNavigate = () => {
        if (!testCase?.id) return;
        navigate(`/test-case/${testCase?.id}`)
    }

    const handleDelete = () => {
        if (!testCase?.id) return;
        handleOpenDeleteDialog(testCase?.id)
    }

    const handleFeedbackClick = async (event, nextValue) => {
        event.stopPropagation();
        if (!testCase?.id) return;

        try {
            await updateFeedbackAsync({
                testCaseId: testCase.id,
                value: testCase.my_feedback === nextValue ? null : nextValue,
            });
        } catch (error) {
            console.error("Failed to update test case feedback:", error);
            showNotification("Failed to save feedback", "error", "");
        }
    };

    if (!testCase) return null;
    const publicId = testCase.public_id || testCase.publicId || "-";

    return (
        <TableRow
            key={testCase.id}
            className={styles.bodyRow}
            hover
            onClick={handleNavigate}
        >
            <TableCell
                className={styles.bodyCheckboxCell}
                onClick={(e) => e.stopPropagation()}
            >
                <Checkbox
                    size="small"
                    className={styles.bodyCheckbox}
                    checked={checkedIds.includes(testCase.id)}
                    onChange={() => handleCheckboxChange(testCase.id)}
                />
            </TableCell>
            <TableCell className={styles.bodyCell}>{publicId}</TableCell>
            <TableCell className={styles.bodyCell}>
                <Box className={styles.nameCell}>
                    <Typography className={styles.nameId}>{publicId}</Typography>
                    <Typography>{testCase.name}</Typography>
                </Box>
            </TableCell>
            <TableCell className={styles.bodyCell}>
                {formatDate(testCase.created_at)}
            </TableCell>
            <TableCell className={styles.bodyCell}>
                {formatDate(testCase.updated_at)}
            </TableCell>
            <TableCell className={styles.bodyCell}>
                <Box className={styles.bodyStatusContainer}>
                    <Tooltip
                        title={isInitialFailure ? failureTooltip : ""}
                        disableHoverListener={!isInitialFailure}
                        PopperProps={{
                            sx: {
                                "& .MuiTooltip-tooltip": {
                                    backgroundColor: "primary.main",
                                },
                            },
                        }}
                    >
                        <Box
                            className={styles.bodyStatus}
                            sx={{
                                ...(TEST_STATUS_STYLES[testCase.status] || {}),
                                whiteSpace: "nowrap",
                                fontSize: "12px",
                                lineHeight: 1.2,
                                padding: "4px 10px",
                                maxWidth: "150px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {statusLabel}
                        </Box>
                    </Tooltip>
                    {isInitialFailure && (
                        <Tooltip
                            title={failureTooltip}
                            PopperProps={{
                                sx: {
                                    "& .MuiTooltip-tooltip": {
                                        backgroundColor: "primary.main",
                                    },
                                },
                            }}
                        >
                            <InfoOutlinedIcon
                                fontSize="small"
                                style={{
                                    marginLeft: 6,
                                    color: "#8E1515",
                                    cursor: "pointer",
                                }}
                            />
                        </Tooltip>
                    )}
                </Box>
            </TableCell>
            <TableCell className={clsx(styles.bodyCell, styles.actionBodyCell)}>
                <Box className={styles.actionCellInner}>
                    {isRunning ? (
                        <Tooltip
                            title="Stop"
                            PopperProps={{
                                sx: {
                                    "& .MuiTooltip-tooltip": {
                                        backgroundColor: "primary.main",
                                    },
                                },
                            }}
                        >
                            <span>
                                <IconButton
                                    className={styles.actionButton}
                                    onClick={handleStop}
                                    disabled={isStopPending || disableActions}
                                >
                                    <StopIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    ) : canRun ? (
                        <Tooltip
                            title={runTooltip}
                            PopperProps={{
                                sx: {
                                    "& .MuiTooltip-tooltip": {
                                        backgroundColor: "primary.main",
                                    },
                                },
                            }}
                        >
                            <span>
                                <IconButton
                                    className={styles.actionButton}
                                    onClick={handleRun}
                                    disabled={isRunPending || disableActions}
                                >
                                    <RunIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    ) : (
                        <Box
                            className={styles.disabledActionButton}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <RunIcon />
                        </Box>
                    )}
                    <Tooltip
                        title={isLikeActive ? "Remove like" : "Like"}
                        PopperProps={{
                            sx: {
                                "& .MuiTooltip-tooltip": {
                                    backgroundColor: "primary.main",
                                },
                            },
                        }}
                    >
                        <span>
                            <IconButton
                                className={clsx(
                                    styles.actionButton,
                                    styles.feedbackButton,
                                    isLikeActive && styles.feedbackActive
                                )}
                                onClick={(event) => handleFeedbackClick(event, 1)}
                                disabled={isFeedbackPending || disableActions}
                            >
                                <Badge
                                    badgeContent={Number(testCase?.likes_count) || 0}
                                    color="primary"
                                    max={99}
                                    showZero
                                >
                                    <ThumbUpAltOutlinedIcon
                                        color={isLikeActive ? "primary" : "action"}
                                        fontSize="small"
                                    />
                                </Badge>
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip
                        title={isDislikeActive ? "Remove dislike" : "Dislike"}
                        PopperProps={{
                            sx: {
                                "& .MuiTooltip-tooltip": {
                                    backgroundColor: "primary.main",
                                },
                            },
                        }}
                    >
                        <span>
                            <IconButton
                                className={clsx(
                                    styles.actionButton,
                                    styles.feedbackButton,
                                    isDislikeActive && styles.feedbackActive,
                                    isDislikeActive && styles.feedbackNegative
                                )}
                                onClick={(event) => handleFeedbackClick(event, -1)}
                                disabled={isFeedbackPending || disableActions}
                            >
                                <Badge
                                    badgeContent={Number(testCase?.dislikes_count) || 0}
                                    color="error"
                                    max={99}
                                    showZero
                                >
                                    <ThumbDownAltOutlinedIcon
                                        color={isDislikeActive ? "error" : "action"}
                                        fontSize="small"
                                    />
                                </Badge>
                            </IconButton>
                        </span>
                    </Tooltip>
                    <IconButton
                        className={styles.actionButton}
                        onClick={handleMenuOpen}
                        disabled={isRunning || disableActions}
                    >
                        <MoreVertIcon
                            color="primary"
                            sx={{
                                opacity: isRunning || disableActions ? 0.3 : 1,
                            }}
                        />
                    </IconButton>
                </Box>
                <Menu
                    anchorEl={anchorEl}
                    open={isMenuOpen}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <MenuItem
                        disabled={disableActions}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleMenuClose(e);
                            handleRegenerate();
                        }}
                    >
                        <UpdateIcon sx={{ mr: 2 }} color="primary" />
                        Regenerate Test Case
                    </MenuItem>
                    <MenuItem
                        disabled={disableActions}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleMenuClose(e);
                            setIsRenameModalOpen(true);
                        }}
                    >
                        <EditIcon sx={{ mr: 2 }} color="primary" />
                        Rename Test Case
                    </MenuItem>
                    <MenuItem
                        disabled={disableActions}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleMenuClose(e);
                            handleDelete();
                        }}
                    >
                        <DeleteOutlineOutlinedIcon
                            sx={{ mr: 2 }}
                            color="error"
                        />
                        <Typography color="error">Delete Test Case</Typography>
                    </MenuItem>
                </Menu>
            </TableCell>
            {isRenameModalOpen && (
                <EditTestCaseModal
                    open={isRenameModalOpen}
                    onClose={() => setIsRenameModalOpen(false)}
                    testCase={testCase}
                    journeyId={journeyId}
                />
            )}
        </TableRow>
    );
}

export default TestCaseItem
